import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Check, ChevronDown, Home } from "lucide-react";
import { useLanguage } from "../ui/LanguageProvider";
import { useMenuRoles } from "../auth/MenuRoleContext";
import logoDefault from "../../image/colo/logo-default.png";

/**
 * 사이드바 최상단 제품 스위처(앱 셸 공용) — 로고 + 현재 제품 이름 + chevron 드롭다운.
 * 항목: IT-CONSULTING(/planning/documents) · CRM(/crm/dashboard) · 홈(/home).
 * current 제품은 연파랑 면 + ✓. 계정(내 축)과 분리된 "제품 전환" 축.
 * 제품 항목은 상단 메뉴 권한(topnav:*)이 있을 때만 보인다 — 영역 가드(App.tsx crmArea/itcArea)와 같은 판정.
 * 설정 화면은 제품이 아니라서 목록에서 강조하지 않고, 트리거 옆 이름만 "설정"으로 보여 준다.
 */
type ProductKey = "crm" | "itc" | "settings";

export default function ProductSwitcher({ current, bar }: { current?: ProductKey; bar?: boolean }) {
  const { t } = useLanguage();
  const { visible } = useMenuRoles();
  const showItc = visible("topnav:IT-CONSULTING");
  const showCrm = visible("topnav:CRM");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const currentName = current === "crm" ? "CRM" : current === "itc" ? "IT-CONSULTING" : current === "settings" ? t({ ko: "설정", en: "Settings" }) : "";
  const rowClass = (active: boolean) =>
    `flex h-[34px] items-center gap-2.5 rounded-control px-2.5 text-[13px] no-underline ${active ? "bg-primary-subtle font-semibold text-primary-strong" : "font-medium text-ink-data hover:bg-surface-hover hover:text-ink"}`;
  // bar 모드: 상단 바에 얹힐 때 자동 폭(사이드바 헤더는 flex:1 전폭).
  return (
    <div className={`ds-scope relative ${bar ? "flex-none" : "min-w-0 flex-1"}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t({ ko: "제품 전환", en: "Switch product" })}
        className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-control pl-1 pr-1.5 hover:bg-surface-hover"
      >
        <img src={logoDefault} alt="Colosseum Vault" className="block h-4 flex-shrink-0" />
        {currentName ? <span className="truncate text-[12px] font-medium text-ink-muted">{currentName}</span> : null}
        <ChevronDown size={14} className="flex-shrink-0 text-ink-muted transition-transform duration-150" style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+6px)] z-50 w-[224px] rounded-card border border-hairline-strong bg-white p-1.5"
          style={{ boxShadow: "0 8px 24px rgba(31,42,68,.10)" }}
        >
          {showItc || showCrm ? <div className="px-2.5 pb-1 pt-1.5 text-[12px] font-semibold text-ink-muted">{t({ ko: "제품", en: "Products" })}</div> : null}
          {showItc ? (
            <NavLink to="/planning/documents" role="menuitem" onClick={() => setOpen(false)} className={rowClass(current === "itc")}>
              IT-CONSULTING{current === "itc" ? <Check size={14} className="ml-auto" aria-hidden /> : null}
            </NavLink>
          ) : null}
          {showCrm ? (
            <NavLink to="/crm/dashboard" role="menuitem" onClick={() => setOpen(false)} className={rowClass(current === "crm")}>
              CRM{current === "crm" ? <Check size={14} className="ml-auto" aria-hidden /> : null}
            </NavLink>
          ) : null}
          {showItc || showCrm ? <div className="mx-1 my-1.5 h-px bg-hairline" /> : null}
          <NavLink to="/home" role="menuitem" onClick={() => setOpen(false)} className={rowClass(false)}>
            <Home size={16} className="text-ink-muted" aria-hidden />
            {t({ ko: "홈", en: "Home" })}
          </NavLink>
        </div>
      ) : null}
    </div>
  );
}
